import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { adminApi, setAdminAccessToken, type AdminProfile, type AdminSession } from './adminApi';

interface AdminAuthState {
  admin: AdminProfile | null;
  /** True until the initial silent refresh settles, so the guard does not flash. */
  loading: boolean;
  signIn: (session: AdminSession) => void;
  signOut: () => Promise<void>;
  /** Re-reads the profile after a change made elsewhere in the portal. */
  refreshProfile: () => Promise<void>;
}

const AdminAuthContext = createContext<AdminAuthState | null>(null);

/**
 * A non-secret hint that an admin session *might* exist.
 *
 * Its own key, separate from the customer app's. The refresh cookie is HttpOnly
 * so the page cannot see whether it holds one, and `/admin/auth/refresh` sits on
 * the strict rate-limit tier because it mints credentials — so probing it on
 * every load would let an ordinary visitor who wandered to `/admin` burn the
 * sign-in budget and lock themselves out for five minutes.
 *
 * The flag grants nothing. Forging it costs one failed refresh; clearing it
 * costs one extra sign-in.
 */
const ADMIN_SESSION_HINT = 'wave.admin.session';

function hasHint(): boolean {
  try {
    return window.localStorage.getItem(ADMIN_SESSION_HINT) === '1';
  } catch {
    // Storage can be unavailable in private modes. Assuming a session keeps
    // such a browser able to sign in.
    return true;
  }
}

function setHint(present: boolean): void {
  try {
    if (present) {
      window.localStorage.setItem(ADMIN_SESSION_HINT, '1');
    } else {
      window.localStorage.removeItem(ADMIN_SESSION_HINT);
    }
  } catch {
    /* storage unavailable; the hint is optional by design */
  }
}

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const [admin, setAdmin] = useState<AdminProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    if (!hasHint()) {
      setLoading(false);
      return;
    }

    void (async () => {
      try {
        const session = await adminApi.refresh();
        if (!cancelled) {
          setAdminAccessToken(session.access_token);
          setAdmin(session.admin);
        }
      } catch {
        if (!cancelled) {
          setAdminAccessToken(null);
          setHint(false);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = useCallback((session: AdminSession) => {
    setAdminAccessToken(session.access_token);
    setAdmin(session.admin);
    setHint(true);
  }, []);

  const signOut = useCallback(async () => {
    try {
      await adminApi.logout();
    } catch {
      // Drop local state regardless. Leaving a signed-out operator looking
      // signed in is worse than a server-side session that expires on its own.
    } finally {
      setAdminAccessToken(null);
      setAdmin(null);
      setHint(false);
    }
  }, []);

  const refreshProfile = useCallback(async () => {
    try {
      setAdmin(await adminApi.me());
    } catch {
      /* the session guard will deal with a dead session on the next call */
    }
  }, []);

  const value = useMemo(
    () => ({ admin, loading, signIn, signOut, refreshProfile }),
    [admin, loading, signIn, signOut, refreshProfile],
  );

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
}

export function useAdminAuth(): AdminAuthState {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used inside an AdminAuthProvider');
  }
  return context;
}
